<template>
    <div
        class="string-cell"
        :class="[field.alignment, { 'invalid-value': invalidValue, outlined: outlined, readonly: readonly }]"
        @mouseenter="showOverlay = true"
        @mouseleave="showOverlay = false"
    >
        <!-- Düzenlenmeyen (Görüntüleme) Modu -->
        <template v-if="!editing">
            <a-tooltip :title="value">
                <div class="text-wrapper">
                    {{ value }}
                </div>
            </a-tooltip>
        </template>

        <!-- Düzenleme Modu -->
        <template v-else>
            <input
                type="text"
                v-model="value"
                @blur="debouncedDisableEdit"
                @keyup.enter="debouncedDisableEdit"
                ref="input"
                class="borderless-input"
                :class="{ 'invalid-input': invalidValue }"
            />
        </template>

        <!-- Hover Overlay'de Aksiyon İkonları -->
        <div v-if="!editing && showOverlay && !readonly" class="overlay-actions">
            <a-button
                icon="link"
                size="small"
                @click="openUrlInNewTab"
                v-if="isUrlField && value"
                style="margin-right: 4px"
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                content="URL'yi aç"
            ></a-button>
            <PermissionController :permissions="['table.update']">
                <a-button
                    v-if="aiMode"
                    icon="thunderbolt"
                    size="small"
                    @click="runAiField"
                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                    content="Yapay Zeka'yı çalıştır"
                    style="margin-right: 4px"
                ></a-button>
            </PermissionController>
            <PermissionController :permissions="['table.update']">
                <a-button
                    icon="edit"
                    size="small"
                    @click="enableEdit"
                    style="margin-right: 4px"
                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                    content="Düzenle"
                ></a-button>
            </PermissionController>
            <PermissionController :permissions="['table.update']">
                <a-button
                    icon="delete"
                    type="danger"
                    size="small"
                    @click="deleteValue"
                    style="margin-right: 4px"
                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                    content="Sil"
                ></a-button>
            </PermissionController>
            <a-button
                icon="mail"
                size="small"
                @click="openMailComposerModal"
                v-if="isEmailField"
                style="margin-right: 4px"
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                content="E-posta gönder"
            ></a-button>

            <a-button size="small" @click="openWhatsappModal" class="whatsapp-button" v-if="isPhoneField">
                <WhatsappIcon />
            </a-button>
        </div>

        <!-- WhatsApp Chat Starter Modal -->
        <Modal2
            v-if="showWhatsappChatStarterModal"
            @cancel="showWhatsappChatStarterModal = false"
            :title="$t('whatsappChatStart')"
            :showFooter="false"
            className="whatsapp-chat-starter-modal keydown-prevent-modal"
        >
            <WhatsappChatStarter
                :default-phone-number="value"
                channel-override
                phone-number-override
                @close="showWhatsappChatStarterModal = false"
            />
        </Modal2>

        <!-- 7) Mail Composer Modal -->
        <Modal2
            v-if="showMailComposerModal"
            @cancel="showMailComposerModal = false"
            :title="$t('mail.composeNewEmail')"
            :showFooter="false"
            xlarge
            className="table-mail-composer-modal"
        >
            <MailCompose
                @close="showMailComposerModal = false"
                @sent="showMailComposerModal = false"
                :show-header="false"
                :mail-data="mailComposerData"
                :enabled-select-sender="true"
                :selected-email-id="defaultMailId"
            />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import debounce from "lodash/debounce";
import Modal2 from "@/components/common/Modal2.vue";
import WhatsappChatStarter from "@/views/Pages/Integrations/meta-leads/WhatsappChatStarter.vue";
import WhatsappIcon from "../../../../../components/svg-new-ui/WhatsappIcon.vue";
import { Tooltip } from "ant-design-vue";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import MailCompose from "@/views/Pages/Mail/new/components/MailCompose.vue";
import { mapGetters } from "vuex";

export default Vue.extend({
    name: "StringCell",
    components: {
        Modal2,
        WhatsappChatStarter,
        WhatsappIcon,
        MailCompose,
        "a-tooltip": Tooltip,
    },
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        aiMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editing: false,
            value: this.record.data[this.field._id] || "",
            invalidValue: false,
            showOverlay: false,
            showWhatsappChatStarterModal: false,
            showMailComposerModal: false,
            mailComposerData: {
                to: this.record.data[this.field._id] || "",
                cc: "",
                bcc: "",
                subject: "",
                body: "",
            },
        };
    },
    computed: {
        ...mapGetters({
            defaultMail: "mail/defaultMail",
        }),
        defaultMailId() {
            return this.defaultMail ? this.defaultMail._id : "";
        },
        isPhoneField() {
            return this.field.fieldType === "phone";
        },
        isEmailField() {
            return this.field.fieldType === "email";
        },
        isUrlField() {
            return this.field.fieldType === "url";
        },
    },
    created() {
        this.debouncedDisableEdit = debounce(this.disableEdit, 300);
    },
    methods: {
        enableEdit() {
            console.log("enableEdit", { value: this.value });
            if (this.readonly) {
                return;
            }
            this.editing = true;
            this.$nextTick(() => {
                if (this.$refs.input) {
                    this.$refs.input.focus();
                }
            });
        },
        async disableEdit() {
            console.log("disableEdit", { value: this.value });

            this.editing = false;
            try {
                const isChanged = this.record.data[this.field._id] !== this.value;
                if (isChanged) {
                    const isValid = this.validateValue(this.value, this.field.fieldType);
                    if (isValid) {
                        this.invalidValue = false;
                        await this.$emit("update-record", {
                            recordId: this.record._id,
                            fieldId: this.field._id,
                            value: this.value,
                        });
                    } else {
                        this.invalidValue = true;
                        this.$message.error("Geçersiz değer. Lütfen doğru formatı kullanın.");
                    }
                }
            } catch (error) {
                console.error("Kayıt güncellenirken hata oluştu:", error);
                this.$message.error("Kayıt güncellenirken hata oluştu.");
            }
        },
        deleteValue() {
            console.log("deleteValue", { value: this.value });
            // İçeriği silip günceller
            this.value = "";
            this.invalidValue = false;
            this.$emit("update-record", {
                recordId: this.record._id,
                fieldId: this.field._id,
                value: "",
            });
        },
        openWhatsappModal() {
            // Sahada telefon numarası değilse kullanıcıya uyarı gösterelim
            if (this.field.fieldType !== "phone") {
                this.$message.error("Bu alan telefon numarası için geçerlidir.");
                return;
            }
            this.showWhatsappChatStarterModal = true;
        },
        openMailComposerModal() {
            if (this.field.fieldType !== "email") {
                this.$message.error("Bu alan e-posta için geçerlidir.");
                return;
            }
            this.showMailComposerModal = true;
        },
        openUrlInNewTab() {
            if (this.field.fieldType !== "url") {
                this.$message.error("Bu alan URL için geçerlidir.");
                return;
            }

            let url = this.value;
            // URL'nin http:// veya https:// ile başlayıp başlamadığını kontrol et
            if (!/^https?:\/\//i.test(url)) {
                url = "https://" + url;
            }

            window.open(url, "_blank");
        },
        validateValue(value, fieldType) {
            console.log("validateValue", { value, fieldType });
            switch (fieldType) {
                case "email":
                    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                case "phone":
                    return /^\+?[0-9]{6,14}$/.test(value);
                case "url":
                    const regext = RegExp(/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/);
                    return regext.test(value);
                case "color":
                    return /^(#(?:[0-9a-f]{2}){2,4}$|(#[0-9a-f]{3}$)|(rgb|hsl)a?\((-?\d+%?[,\s]+){2,3}\s*[\d\.]+%?\)$)/i.test(
                        value
                    );
                default:
                    return true;
            }
        },
        runAiField() {
            sendSocketMessage("automation table-run-ai-filed", {
                siteId: this.$store.state.site.site._id,
                tableId: this.field.table,
                recordId: this.record._id,
                fieldId: this.field._id,
                userId: this.$store.state.auth.user._id,
            });
        },
    },
});
</script>

<style scoped lang="scss">
.string-cell {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    transition: all 0.3s;
    position: relative;

    &.outlined {
        border: 1px solid #d9d9d9;
        border-radius: 4px;

        &:not(.readonly) {
            cursor: pointer;

            &:hover {
                border-color: #40a9ff;
            }

            &:active {
                border-color: #096dd9;
            }
        }

        &.readonly {
            background-color: #f5f5f5;
            border-color: #d9d9d9;
            cursor: not-allowed;
        }
    }

    &.left {
        justify-content: flex-start;
    }

    &.center {
        justify-content: center;
    }

    &.right {
        justify-content: flex-end;
    }

    &.invalid-value {
        background-color: #ffebee;
    }

    .borderless-input {
        border: none;
        outline: none;
        background: transparent;
        width: 100%;
        text-align: inherit;

        &.invalid-input {
            border: 1px solid #f44336;
        }
    }

    .text-wrapper {
        width: 100%;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .overlay-actions {
        position: absolute;
        top: 2px;
        right: 2px;
        display: flex;
        gap: 4px;

        .whatsapp-button {
            display: flex;
            align-items: center;
            justify-content: center;

            svg {
                width: 16px;
                height: 16px;
            }
        }
    }
}
</style>
<style lang="scss">
.table-mail-composer-modal {
    #mail-composer {
        padding: 0 !important;

        .title-wrapper {
            display: none;
        }

        .email-editor {
            .body {
                padding: 0 !important;
            }
        }
    }
}
</style>
